import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../../../components/Loader';
import { useQueryQueueContext } from '../../../contexts/QueryQueueContext';

export type ConfirmActionDialogProps = {
  title: string;
  content: () => JSX.Element;
  onConfirm: () => void;
  onClose: () => void;
  opened: boolean;
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  isOfflineMode?: boolean;
  error?: string;
};

const ConfirmActionDialog = ({
  title,
  content,
  onConfirm,
  opened,
  onClose,
  isLoading,
  isError,
  isSuccess,
  isOfflineMode,
  error,
}: ConfirmActionDialogProps) => {
  const [open, setOpen] = useState<boolean>(opened);
  const { queryAdded, setQueryAdded } = useQueryQueueContext();
  const { t } = useTranslation();

  useEffect(() => {
    setOpen(opened);
  }, [opened]);

  const handleOnClose = () => {
    setQueryAdded(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        {!isError && isLoading ? (
          <Loader />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 40,
              height: 40,
              backgroundColor: isSuccess ? null : 'primary.main',
              color: isSuccess ? 'success.main' : 'white',
              border: isSuccess ? '1px solid' : null,
              borderColor: isSuccess ? 'success.main' : null,
              borderRadius: '50%',
              justifyContent: 'center',
            }}
          >
            {content()}
          </Box>
        )}

        {isOfflineMode && (
          <Typography sx={{ marginTop: 2, color: 'error.main', fontWeight: 600 }}>{t('errors.offlineMode')}</Typography>
        )}
        {isError && !isOfflineMode && (
          <Typography sx={{ marginTop: 2, color: 'error.main', fontWeight: 600 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        {(isOfflineMode ? !queryAdded : !isSuccess) && (
          <Button onClick={onConfirm} disabled={isLoading && !isError}>
            {isError ? t('retry') : t('confirm')}
          </Button>
        )}

        {(isOfflineMode ? !queryAdded : !isSuccess) && (
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
        )}

        {(isOfflineMode ? queryAdded : isSuccess) && <Button onClick={handleOnClose}>{t('close')}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmActionDialog;
